import React, { useCallback } from "react";
import {
  Button,
  FormGroup,
  CircularProgress,
  TextField,
  Dialog,
  Box,
} from "@mui/material";
import userSupportService from "../../../services/UserSupportService";
import { useFormik } from "formik";
import { LookupUserDTOV1 } from "@beable/roster-backend-client-v1";
import { useTheme } from "@mui/material/styles";
import { getStyles } from "./styles";

type ContactInfoModalProps = {
  profile: LookupUserDTOV1;
  open: boolean;
  handleClose: () => void;
};

type ContactInfoFormProps = Omit<ContactInfoModalProps, "open" >;

const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;

export default function ContactInfoModal({ profile, open, handleClose }: ContactInfoModalProps) {
  return (
    <>
      <Dialog
        aria-labelledby="contact-info-modal-title"
        aria-describedby="contact-info-modal-description"
        open={open}
        onClose={(_event, reason) => {
          if (reason !== "backdropClick") {
            handleClose();
          }
        }}
        scroll="body"
      >
        { profile && <ContactInfoForm profile={profile} handleClose={handleClose}/> }
      </Dialog>
    </>
  );
}

function ContactInfoForm({ profile, handleClose }: ContactInfoFormProps) {
  const theme = useTheme();
  const styles = getStyles(theme);

  const validate = useCallback(({ userProvidedEmail }: { userProvidedEmail: string }) => {
    if (userProvidedEmail && !emailRegex.test(userProvidedEmail)) {
      return { userProvidedEmail: "Invalid email. Please try again." };
    }
    return {};
  }, []);

  const handleSubmit = async () => {
    if (formik.values.userProvidedEmail) {
      const success = await userSupportService.updateUserEmail(
        profile.userAccountId!,
        profile.siteId,
        formik.values.userProvidedEmail
      );

      if (success) {
        profile.email = formik.values.userProvidedEmail;
        if (profile.contactInfo) {
          profile.contactInfo.userProvidedEmail = formik.values.userProvidedEmail;
        }
        handleClose();
      } else {
        formik.setErrors({ userProvidedEmail: 'Update Failed.' });
      }
    }
  };

  const formik = useFormik({
    initialValues: { userProvidedEmail: profile?.contactInfo?.userProvidedEmail ?? "" },
    onSubmit: handleSubmit,
    validate,
    validateOnMount: true,
    enableReinitialize: true
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Box sx={styles.paper}>
        <h2 id="contact-info-modal-title">Contact Info</h2>
        <FormGroup sx={[styles.mt2, styles.dfr]}>
          <Box sx={styles.box}>
            First Name: {profile.firstName} <br />
            Last Name: {profile.lastName} <br />
            Username: {profile.username} <br />
            Roster-provided email address: { profile.contactInfo?.rosterEmail ?? 'N/A' } <br />
            User-provided email address: {" "}
            <TextField
              variant="standard"
              margin="none"
              id="userProvidedEmail"
              name="userProvidedEmail"
              type="email"
              sx={{ width: "275px" }}
              value={formik.values.userProvidedEmail}
              onChange={formik.handleChange}
              error={!!formik.errors?.userProvidedEmail}
              helperText={formik.errors?.userProvidedEmail}
              slotProps={{
                htmlInput: {
                  style: styles.inlineField,
                }
              }}
            />
          </Box>
          <Button
            variant="contained"
            color="secondary"
            type="submit"
            disabled={ formik.isSubmitting || !formik.values.userProvidedEmail || !!formik.errors.userProvidedEmail || !formik.dirty }
          >
            {formik.isSubmitting && (
              <>
                <CircularProgress data-testid="progress" size={8} />
                &nbsp;
              </>
            )}
            Save
          </Button>
          <Button variant="contained" onClick={handleClose} sx={styles.ml2}>
            Close
          </Button>
        </FormGroup>
      </Box>
    </form>
  );
}